/**
 * Audit ZA — useAuth Hook.
 *
 * Stellt aktuellen Auth-Zustand (Session, User, Status) reaktiv bereit.
 * Initialisiert die Session beim Mount, abonniert onAuthChange für
 * Live-Updates (Login, Logout, Token-Refresh).
 */

import { useEffect, useState } from 'react';

import {
  getCachedSession,
  initAuth,
  onAuthChange,
  type Session,
} from '@/lib/auth';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface UseAuthState {
  status: AuthStatus;
  session: Session | null;
}

export function useAuth(): UseAuthState {
  const cached = getCachedSession();
  const [session, setSession] = useState<Session | null>(cached);
  // Wenn schon Cached-Session da → direkt authenticated, sonst loading bis initAuth fertig.
  const [status, setStatus] = useState<AuthStatus>(
    cached ? 'authenticated' : 'loading'
  );

  useEffect(() => {
    let mounted = true;

    void initAuth().then((s) => {
      if (!mounted) return;
      setSession(s);
      setStatus(s ? 'authenticated' : 'anonymous');
    });

    const unsubscribe = onAuthChange((s) => {
      if (!mounted) return;
      setSession(s);
      setStatus(s ? 'authenticated' : 'anonymous');
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return { status, session };
}
